'use client';

import type { TFunction } from 'i18next';
import type { BusinessBranchItem } from '@/lib/backend-api';

/** Revenue by business branch (top level, children indented). The caller
 *  renders it only when more than one branch carries a revenue. */
export function ProfileBusinessBranches({
  businessBranches,
  t,
}: {
  businessBranches: BusinessBranchItem[];
  t: TFunction;
}) {
  const withRevenue = businessBranches.filter(
    (b) => b.revenue != null,
  );
  const parentNames = new Set(
    withRevenue.map((b) => b.parent_name).filter(Boolean),
  );
  // Top-level: either has no parent_name, or its parent_name is not itself a branch name
  const branchNames = new Set(withRevenue.map((b) => b.name));
  const topLevel = withRevenue.filter(
    (b) => !b.parent_name || !branchNames.has(b.parent_name),
  );
  const children = (parentName: string) =>
    withRevenue.filter((b) => b.parent_name === parentName);
  const fmtBranch = (v: number, unit?: string | null) => {
    if (unit) {
      return `${Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(v)} ${unit}`;
    }
    return Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(v);
  };
  return (
    <div className="space-y-3 pt-4 border-t">
      <h2 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground">
        {t('companyDetail.businessSectors')}
      </h2>
      <div className="flex flex-col gap-1">
        {topLevel.map((branch, idx) => (
          <div key={idx}>
            <div className="flex items-center justify-between gap-4 text-xs sm:text-sm">
              <span
                className={
                  parentNames.has(branch.name)
                    ? 'font-medium text-foreground'
                    : 'text-foreground'
                }
              >
                {branch.name}
              </span>
              <span className="text-muted-foreground font-medium shrink-0">
                {fmtBranch(branch.revenue!, branch.revenue_unit)}
                {branch.percentage_of_total != null && (
                  <span className="ml-1.5 text-xs text-muted-foreground">
                    ({branch.percentage_of_total.toFixed(1)}%)
                  </span>
                )}
              </span>
            </div>
            {children(branch.name).map((child, cidx) => (
              <div
                key={cidx}
                className="flex items-center justify-between gap-4 text-xs sm:text-sm pl-4 mt-0.5"
              >
                <span className="text-muted-foreground">
                  {child.name}
                </span>
                <span className="text-muted-foreground font-medium shrink-0">
                  {fmtBranch(child.revenue!, child.revenue_unit)}
                  {child.percentage_of_total != null && (
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      ({child.percentage_of_total.toFixed(1)}%)
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
